"use client";

import * as React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  num: "01",
  title: "Source Analysis",
  description: "We ingest your existing documentation, ticket history, and knowledge base. Our AI maps the complex data architecture of your IT environment to ensure every support agent is fully context-aware from day one."
}, {
  num: "02",
  title: "Agent Training",
  description: "Using advanced neural fine-tuning, we train your dedicated AI agents on your specific protocols. This phase ensures the agents can solve complicated support cases with the same nuance as your top-tier human experts."
}, {
  num: "03",
  title: "API Integration",
  description: "We deploy secure, high-speed connections between our AI core and your existing tech stack. This creates a seamless flow of information, allowing for real-time problem solving and automated ticket resolution."
}, {
  num: "04",
  title: "Full Autonomy",
  description: "Your AI support ecosystem goes live, operating with 24/7 efficiency. Experience a drastic reduction in operational costs while maintaining a high-performance, autonomous support infrastructure."
}];
function StepItem({
  step,
  isLast,
  index
}: {
  step: (typeof steps)[0];
  isLast: boolean;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className={`flex gap-6 md:gap-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="flex flex-col items-center shrink-0 w-16 md:w-24">
        <div className="text-5xl md:text-7xl font-default font-bold text-primary leading-none tracking-tighter drop-shadow-[0_0_15px_rgba(0,255,255,0.3)]">
          {step.num}
        </div>
        {!isLast && <div className="w-px grow border-l-2 border-dashed border-primary/40 my-6 min-h-[4rem] md:min-h-[6rem]" />}
      </div>

      {}
      <div className={`pt-2 ${!isLast ? "pb-12 md:pb-20" : ""}`}>
        <h3 className="text-2xl md:text-3xl font-default font-semibold text-foreground mb-4 tracking-tight">
          {step.title}
        </h3>
        <p className="text-muted-foreground font-mono text-sm md:text-base leading-relaxed max-w-2xl">
          {step.description}
        </p>
      </div>
    </div>;
}
export default function TheIntegrationRoadmap() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223290" id="the-integration-roadmap" className="bg-background py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        {}
        <div ref={headerRef} className={`mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight uppercase">
            The Integration Roadmap
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl font-sans">
            A high-performance deployment strategy designed to transition your IT support 
            from human-dependent to fully autonomous AI systems.
          </p>
        </div>

        {}
        <div className="flex flex-col">
          {steps.map((step, i) => <StepItem key={step.num} step={step} index={i} isLast={i === steps.length - 1} />)}
        </div>

        {}
        <div className="mt-16 md:mt-24 ml-22 md:ml-36">
          <Button asChild size="lg" className="rounded-lg px-8 bg-primary text-primary-foreground hover:shadow-[0_0_20px_rgba(0,255,255,0.5)] transition-all duration-300">
            <Link to="/ServiceTiersPage">
              Deploy Your Agent
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}